'use client'

import { getCommunitiesByOwner } from '@/actions/community'
import ImageWithFallback from '@/components/common/image-with-fallback'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { ChevronDown } from 'lucide-react'
import { useRouter } from 'nextjs-toploader/app'
import { useMemo, useState } from 'react'
import useSWRImmutable from 'swr/immutable'

export default function CommunityDisplay({
  session,
  community,
  communityLoading,
}) {
  const { data: communities, isLoading } = useSWRImmutable(
    session?.user?.id,
    getCommunitiesByOwner,
  )

  const [open, setOpen] = useState(false)

  const router = useRouter()

  const myCommunities = useMemo(() => {
    return communities?.communities?.filter((c) => c.id !== community?.id) || []
  }, [communities?.communities, community?.id])

  const adminCommunities = useMemo(() => {
    return (
      communities?.adminCommunities?.filter((c) => c.id !== community?.id) || []
    )
  }, [communities?.adminCommunities, community?.id])

  if (communityLoading)
    return (
      <div className="flex w-full flex-col items-center gap-2 rounded-sm border border-neutral-blue-700 bg-neutral-blue-900 py-5">
        <Skeleton className="aspect-square size-10 overflow-hidden rounded-sm border border-neutral-blue-700"></Skeleton>
        <Skeleton className="h-5 w-1/2 rounded-sm"></Skeleton>
      </div>
    )

  if (!community) return null

  const hasOtherCommunities =
    session && myCommunities.length + adminCommunities.length > 0

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          className={cn(
            'flex w-full flex-col items-center gap-2 text-wrap rounded-sm border border-neutral-blue-700 bg-neutral-blue-900 px-1 py-5 text-center',
            {
              'cursor-pointer': hasOtherCommunities,
            },
          )}
        >
          <div className="relative aspect-square size-10 overflow-hidden rounded-sm border border-neutral-blue-700">
            <ImageWithFallback src={community.logoImgUrl} fill />
          </div>
          <div className="flex items-center gap-1">
            <p className="text-p-2 font-semibold">{community.name}</p>
            {hasOtherCommunities && (
              <ChevronDown
                className={cn('size-4 transition-transform duration-200', {
                  'rotate-180': open,
                })}
              />
            )}
          </div>
        </div>
      </PopoverTrigger>
      {!isLoading && hasOtherCommunities && (
        <PopoverContent className="w-[300px] p-0">
          <Command>
            <CommandInput placeholder={`Search communities...`} />
            <CommandList>
              <CommandEmpty>No Community found.</CommandEmpty>
              {myCommunities.length > 0 && (
                <CommandGroup heading="My Communities">
                  {myCommunities.map((c) => (
                    <CommandItem
                      onSelect={() => {
                        setOpen(false)
                        router.push(`/manage/${c.id}/racing`)
                      }}
                      key={c.id}
                      value={`${c.name} ${c.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <ImageWithFallback
                          src={c.logoImgUrl}
                          height={32}
                          width={32}
                          className="rounded-sm"
                        />
                        <p>{c.name}</p>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {adminCommunities.length > 0 && (
                <CommandGroup heading="Admin Communities">
                  {adminCommunities.map((c) => (
                    <CommandItem
                      onSelect={() => {
                        setOpen(false)
                        router.push(`/manage/${c.id}/racing`)
                      }}
                      key={c.id}
                      value={`${c.name} ${c.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <ImageWithFallback
                          src={c.logoImgUrl}
                          height={32}
                          width={32}
                          className="rounded-sm"
                        />
                        <p>{c.name}</p>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      )}
    </Popover>
  )
}
